import {
  deserify,
  getLocalStorage,
  useCommonServiceClientContext,
  useFeatureEnabled,
} from '@geneo2-web/shared-ui';
import { ContentLockStatusType, Feature } from '@protos/school_management/school.db_pb';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { useAppSelector, useDeserifiedSelector } from '../../../../reduxStore/reduxHooks';

export interface LockedIds {
  resourceIds: Set<string>;
  lessonIds: Set<string>;
}

const EMPTY: LockedIds = { resourceIds: new Set(), lessonIds: new Set() };

// Locked content can't be opened by students, and the backend rejects it.
// This only greys it out early; on failure we return nothing and let the server decide.
export const useContentLocks = () => {
  const teacher_id = getLocalStorage('userId');
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const class_subject_info = useDeserifiedSelector(
    (state) => state.homeDashboard.class_subject_info
  );
  const { LmsCommonAPIServiceV1ClientWithStatusCodeHandler } =
    useCommonServiceClientContext();
  const isContentLockFeature = useFeatureEnabled(
    user_info?.schoolDetails[0]?.featuresPlanInfo,
    Feature.CONTENT_LOCK
  );

  return async (resourceIds: string[], lessonIds: string[]) => {
    if (!isContentLockFeature || (!resourceIds.length && !lessonIds.length)) {
      return EMPTY;
    }
    try {
      const response =
        await LmsCommonAPIServiceV1ClientWithStatusCodeHandler.getLockStatusByIds(
          {
            personId: BigInt(teacher_id),
            personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
            subjectId: class_subject_info?.subjectId,
            schoolClassSectionId: class_subject_info?.sectionId,
            resourceIds,
            lessonIds,
          }
        );
      return {
        resourceIds: new Set(
          (response?.resourceInfo ?? [])
            .filter(
              (r) =>
                r.lockStatus === ContentLockStatusType.CONTENT_LOCK_STATUS_IS_LOCKED
            )
            .map((r) => r.resourceId)
        ),
        // A lesson with any locked resource can't be assigned as a whole.
        lessonIds: new Set(
          (response?.lessonInfo ?? [])
            .filter(
              (l) =>
                l.lockStatus ===
                  ContentLockStatusType.CONTENT_LOCK_STATUS_IS_LOCKED ||
                l.lockStatus ===
                  ContentLockStatusType.CONTENT_LOCK_STATUS_IS_PARTIALLY_LOCKED ||
                l.lockedResourceIds.length > 0
            )
            .map((l) => l.lessonId)
        ),
      };
    } catch (err) {
      console.log(err);
      return EMPTY;
    }
  };
};
